import type { Point, LineString, Polygon, Position } from 'geojson';
import type { OperationalFeature, FeatureProperties, FeatureType } from '@/types/operational';

/**
 * Generate a unique feature ID
 */
export function generateFeatureId(): string {
  return `feature-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Create a point feature
 */
export function createPointFeature(
  coordinates: Position,
  style: FeatureProperties['style'],
  userId: string,
  layerId: string,
  name?: string
): OperationalFeature {
  const now = Date.now();
  const id = generateFeatureId();

  return {
    type: 'Feature',
    geometry: {
      type: 'Point',
      coordinates,
    } as Point,
    properties: {
      id,
      name: name || `Point ${id.substring(0, 8)}`,
      featureType: 'point',
      layerId,
      style,
      createdBy: userId,
      createdAt: now,
      updatedAt: now,
    },
  };
}

/**
 * Create a line feature
 */
export function createLineFeature(
  coordinates: Position[],
  style: FeatureProperties['style'],
  userId: string,
  layerId: string,
  name?: string
): OperationalFeature {
  const now = Date.now();
  const id = generateFeatureId();

  return {
    type: 'Feature',
    geometry: {
      type: 'LineString',
      coordinates,
    } as LineString,
    properties: {
      id,
      name: name || `Line ${id.substring(0, 8)}`,
      featureType: 'line',
      layerId,
      style,
      createdBy: userId,
      createdAt: now,
      updatedAt: now,
    },
  };
}

/**
 * Create a polygon feature
 */
export function createPolygonFeature(
  coordinates: Position[],
  style: FeatureProperties['style'],
  userId: string,
  layerId: string,
  featureType: FeatureType = 'polygon',
  name?: string
): OperationalFeature {
  const now = Date.now();
  const id = generateFeatureId();

  // Ensure polygon is closed
  const closedCoords = [...coordinates];
  if (
    coordinates.length > 0 &&
    (coordinates[0][0] !== coordinates[coordinates.length - 1][0] ||
      coordinates[0][1] !== coordinates[coordinates.length - 1][1])
  ) {
    closedCoords.push(coordinates[0]);
  }

  return {
    type: 'Feature',
    geometry: {
      type: 'Polygon',
      coordinates: [closedCoords],
    } as Polygon,
    properties: {
      id,
      name: name || `${featureType.charAt(0).toUpperCase() + featureType.slice(1)} ${id.substring(0, 8)}`,
      featureType,
      layerId,
      style,
      createdBy: userId,
      createdAt: now,
      updatedAt: now,
    },
  };
}

/**
 * Create a rectangle from two corner points
 */
export function createRectangleCoordinates(
  start: Position,
  end: Position
): Position[] {
  return [
    [start[0], start[1]],
    [end[0], start[1]],
    [end[0], end[1]],
    [start[0], end[1]],
    [start[0], start[1]], // Close the polygon
  ];
}

/**
 * Create a circle approximation as a polygon
 */
export function createCircleCoordinates(
  center: Position,
  radiusInMeters: number,
  numPoints: number = 64
): Position[] {
  const coordinates: Position[] = [];
  const latRadius = radiusInMeters / 111320;
  // Correct for longitude compression at this latitude
  const lngRadius = latRadius / Math.cos(center[1] * Math.PI / 180);

  for (let i = 0; i <= numPoints; i++) {
    const angle = (i / numPoints) * 2 * Math.PI;
    const lng = center[0] + lngRadius * Math.cos(angle);
    const lat = center[1] + latRadius * Math.sin(angle);
    coordinates.push([lng, lat]);
  }

  return coordinates;
}

/**
 * Calculate distance between two points in meters
 */
export function calculateDistance(p1: Position, p2: Position): number {
  const R = 6371000; // Earth's radius in meters
  const lat1 = (p1[1] * Math.PI) / 180;
  const lat2 = (p2[1] * Math.PI) / 180;
  const deltaLat = ((p2[1] - p1[1]) * Math.PI) / 180;
  const deltaLng = ((p2[0] - p1[0]) * Math.PI) / 180;

  const a =
    Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(deltaLng / 2) * Math.sin(deltaLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c;
}

/**
 * Create a curved ballistic trajectory arc between origin and target.
 * Uses a quadratic bezier with a control point offset perpendicular
 * to the origin→target line, producing a realistic arc on a flat map.
 */
export function createBallisticTrajectory(
  origin: Position,
  target: Position,
  numPoints: number = 80
): Position[] {
  const midLng = (origin[0] + target[0]) / 2;
  const midLat = (origin[1] + target[1]) / 2;

  // Perpendicular offset for the arc bow
  const dx = target[0] - origin[0];
  const dy = target[1] - origin[1];
  const dist = Math.sqrt(dx * dx + dy * dy);
  // Arc height = 25% of distance, perpendicular to the line
  const arcHeight = dist * 0.25;
  // Perpendicular direction (rotate 90° CCW): (-dy, dx) normalized
  const nx = -dy / (dist || 1);
  const ny = dx / (dist || 1);

  const cpLng = midLng + nx * arcHeight;
  const cpLat = midLat + ny * arcHeight;

  const coords: Position[] = [];
  for (let i = 0; i <= numPoints; i++) {
    const t = i / numPoints;
    // Quadratic bezier: B(t) = (1-t)²·P0 + 2(1-t)t·CP + t²·P1
    const mt = 1 - t;
    const lng = mt * mt * origin[0] + 2 * mt * t * cpLng + t * t * target[0];
    const lat = mt * mt * origin[1] + 2 * mt * t * cpLat + t * t * target[1];
    coords.push([lng, lat]);
  }
  return coords;
}

/**
 * Sample N evenly-spaced moving dot positions along a trajectory,
 * offset by a time phase so they animate along the curve.
 */
export function sampleBallisticDots(
  trajectory: Position[],
  numDots: number,
  phase: number // 0..1 cycling value
): Position[] {
  const dots: Position[] = [];
  const len = trajectory.length - 1;
  for (let i = 0; i < numDots; i++) {
    // Each dot is spaced 1/numDots apart, shifted by phase
    let t = ((i / numDots) + phase) % 1;
    const idx = t * len;
    const lo = Math.floor(idx);
    const hi = Math.min(lo + 1, len);
    const frac = idx - lo;
    const lng = trajectory[lo][0] + (trajectory[hi][0] - trajectory[lo][0]) * frac;
    const lat = trajectory[lo][1] + (trajectory[hi][1] - trajectory[lo][1]) * frac;
    dots.push([lng, lat]);
  }
  return dots;
}

/**
 * Create a ballistic feature storing origin, target and impact radius.
 * Geometry is a LineString (curved trajectory arc). Extra metadata
 * in properties stores origin, target, range and impactRadius.
 */
export function createBallisticFeature(
  origin: Position,
  target: Position,
  impactRadius: number,
  style: FeatureProperties['style'],
  userId: string,
  layerId: string
): OperationalFeature {
  const now = Date.now();
  const id = generateFeatureId();
  const range = calculateDistance(origin, target);
  const trajectory = createBallisticTrajectory(origin, target);

  return {
    type: 'Feature',
    geometry: {
      type: 'LineString',
      coordinates: trajectory,
    } as LineString,
    properties: {
      id,
      name: `Ballistic ${id.substring(0, 8)}`,
      featureType: 'ballistic' as FeatureType,
      layerId,
      style,
      createdBy: userId,
      createdAt: now,
      updatedAt: now,
      originLng: origin[0],
      originLat: origin[1],
      targetLng: target[0],
      targetLat: target[1],
      range,
      impactRadius,
    },
  };
}

/**
 * Calculate the center of a polygon
 */
export function calculatePolygonCenter(coordinates: Position[]): Position {
  let sumLng = 0;
  let sumLat = 0;
  const n = coordinates.length;

  for (const coord of coordinates) {
    sumLng += coord[0];
    sumLat += coord[1];
  }

  return [sumLng / n, sumLat / n];
}
